import React, { useState } from 'react';
import { OperaWindow, OperaSection, OperaField, type SideButton } from './OperaWindow';
import { staffAvailabilityApi, type StaffReservation, type DayPart } from '../lib/api';

// Section is what decides which row of the week grid a shift sits in. It is
// stored on the ShiftSlot, never re-derived from the times: Harlots runs
// 2pm-10pm as a day shift and 2pm-12am as a night shift.
const SECTION_OPTIONS: Array<{ value: DayPart; label: string }> = [
  { value: 'DAY',       label: 'Day' },
  { value: 'NIGHT',     label: 'Night' },
  { value: 'GRAVEYARD', label: 'Graveyard' },
];

// ===========================================================================
// Create / Edit / Cancel modals for ShiftSlots.
// All four (create, edit, cancel-shift, reject-decl) share the Opera-window
// chrome used elsewhere in the staff PMS.
// ===========================================================================

interface CreateProps {
  defaultDate: string; // YYYY-MM-DD
  onClose: () => void;
  /** Receives every spot published, since one action can create several. */
  onCreated: (spots: StaffReservation[]) => void;
}

export function CreateReservationModal({ defaultDate, onClose, onCreated }: CreateProps) {
  const [date, setDate] = useState(defaultDate);
  const [section, setSection] = useState<DayPart>('NIGHT');
  const [startTime, setStartTime] = useState('18:00');
  const [endTime, setEndTime] = useState('03:00');
  const [count, setCount] = useState(6);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleCreate() {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) { setError('Pick a date.'); return; }
    if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(startTime)) { setError('Enter a valid start time.'); return; }
    if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(endTime)) { setError('Enter a valid end time.'); return; }
    if (startTime === endTime) { setError('Start and end cannot be the same.'); return; }
    if (count < 1 || count > 50) { setError('Publish between 1 and 50 spots.'); return; }

    setBusy(true); setError(null);
    try {
      const result = await staffAvailabilityApi.createReservation({
        date,
        section,
        startTime,
        endTime,
        count,
        notes: notes.trim() || undefined,
      });
      onCreated(result.spots);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  const sideButtons: SideButton[] = [
    { label: 'Create',  onClick: handleCreate, primary: true, disabled: busy },
    { label: 'Cancel',  onClick: onClose,      disabled: busy },
  ];

  const isOvernight = endTime <= startTime && endTime !== startTime;

  return (
    <OperaWindow title="NEW RESERVATION" onClose={onClose} sideButtons={sideButtons}>
      {error && <div className="modal-error">{error}</div>}

      <OperaSection title="When">
        <OperaField label="Date" required>
          <input
            type="date"
            value={date}
            onChange={e => setDate(e.target.value)}
            autoFocus
          />
        </OperaField>
        <OperaField label="Section" required hint="Which row of the week grid">
          <select
            value={section}
            onChange={e => setSection(e.target.value as DayPart)}
          >
            {SECTION_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </OperaField>
        <OperaField label="Start time" required>
          <input
            type="time"
            value={startTime}
            onChange={e => setStartTime(e.target.value)}
          />
        </OperaField>
        <OperaField label="End time" required hint={isOvernight ? 'Crosses midnight (next day)' : undefined}>
          <input
            type="time"
            value={endTime}
            onChange={e => setEndTime(e.target.value)}
          />
        </OperaField>
      </OperaSection>

      <OperaSection title="Spots">
        <OperaField
          label="How many"
          required
          hint={count === 1 ? 'one opportunity' : `${count} separate opportunities, each editable on its own`}
        >
          <input
            type="number"
            min={1}
            max={50}
            value={count}
            onChange={e => setCount(parseInt(e.target.value, 10) || 0)}
            style={{ width: 80 }}
          />
        </OperaField>
      </OperaSection>

      <OperaSection title="Notes">
        <OperaField label="Internal notes">
          <input
            type="text"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="optional"
            maxLength={200}
          />
        </OperaField>
      </OperaSection>
    </OperaWindow>
  );
}

// ---------------------------------------------------------------------------

interface EditProps {
  shift: StaffReservation;
  onClose: () => void;
  onChanged: () => void;
  /** Hands off to CancelReservationModal — cards in the week grid have no action links. */
  onRequestCancel?: () => void;
}

export function EditReservationModal({ shift, onClose, onChanged, onRequestCancel }: EditProps) {
  const [section, setSection] = useState<DayPart>(shift.section);
  const [startTime, setStartTime] = useState(shift.startTime);
  const [endTime, setEndTime] = useState(shift.endTime);
  const [notes, setNotes] = useState(shift.notes ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSave() {
    if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(startTime)) { setError('Enter a valid start time.'); return; }
    if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(endTime)) { setError('Enter a valid end time.'); return; }
    if (startTime === endTime) { setError('Start and end cannot be the same.'); return; }
    setBusy(true); setError(null);
    try {
      // Edits this spot alone. Spots published in the same batch are separate
      // rows and are not touched.
      await staffAvailabilityApi.patchReservation(shift.id, {
        section,
        startTime,
        endTime,
        notes: notes.trim() || null,
      });
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  const sideButtons: SideButton[] = [
    { label: 'Save',   onClick: handleSave, primary: true, disabled: busy },
    { label: 'Close',  onClick: onClose,    disabled: busy },
  ];
  if (onRequestCancel) {
    sideButtons.push({
      label: 'Cancel shift',
      onClick: onRequestCancel,
      danger: true,
      disabled: busy,
    });
  }

  const isOvernight = endTime <= startTime && endTime !== startTime;

  return (
    <OperaWindow
      title="EDIT RESERVATION"
      status={`${shift.date} · ${shift.holder ? shift.holder.workingName : 'open'}`}
      onClose={onClose}
      sideButtons={sideButtons}
    >
      {error && <div className="modal-error">{error}</div>}

      <OperaSection title="Times">
        <OperaField label="Section" required hint="Which row of the week grid">
          <select
            value={section}
            onChange={e => setSection(e.target.value as DayPart)}
          >
            {SECTION_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </OperaField>
        <OperaField label="Start time" required>
          <input type="time" value={startTime} onChange={e => setStartTime(e.target.value)} autoFocus />
        </OperaField>
        <OperaField label="End time" required hint={isOvernight ? 'Crosses midnight (next day)' : undefined}>
          <input type="time" value={endTime} onChange={e => setEndTime(e.target.value)} />
        </OperaField>
      </OperaSection>

      <OperaSection title="This spot">
        <OperaField label="Held by">
          <span className="opera-readonly-strong">
            {shift.holder ? shift.holder.workingName : 'Open — nobody assigned'}
          </span>
        </OperaField>
      </OperaSection>

      <OperaSection title="Notes">
        <OperaField label="Internal notes">
          <input
            type="text"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            maxLength={200}
          />
        </OperaField>
      </OperaSection>
    </OperaWindow>
  );
}

// ---------------------------------------------------------------------------

interface CancelShiftProps {
  shift: StaffReservation;
  onClose: () => void;
  onChanged: () => void;
}

export function CancelReservationModal({ shift, onClose, onChanged }: CancelShiftProps) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleConfirm() {
    setBusy(true); setError(null);
    try {
      await staffAvailabilityApi.cancelReservation(shift.id, reason.trim() || undefined);
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  const sideButtons: SideButton[] = [
    { label: 'Confirm cancel', onClick: handleConfirm, danger: true, disabled: busy },
    { label: 'Back',           onClick: onClose,        disabled: busy },
  ];

  return (
    <OperaWindow
      title="CANCEL SPOT"
      status={`${shift.date} · ${shift.startTime}-${shift.endTime}`}
      onClose={onClose}
      sideButtons={sideButtons}
    >
      {error && <div className="modal-error">{error}</div>}

      <OperaSection title="Confirm">
        <p style={{ margin: '4px 0 10px', fontSize: 12 }}>
          This cancels this one spot. Other spots published alongside it are
          separate and stay open. {shift.holder && (
            <>
              <strong>{shift.holder.workingName}</strong> has this spot in her week —
              her declaration stays in place. Use a broadcast (Phase 3) to
              reassign her.
            </>
          )}
        </p>
        <OperaField label="Reason">
          <input
            type="text"
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="optional — e.g. low demand, staffing issue"
            autoFocus
            maxLength={200}
          />
        </OperaField>
      </OperaSection>
    </OperaWindow>
  );
}

// ---------------------------------------------------------------------------

interface ApproveProps {
  declarationId: string;
  workingName: string;
  dateLabel: string;
  hoursLabel: string;
  onClose: () => void;
  onApproved: () => void;
}

/**
 * Approving custom hours needs a section: it decides which row of the week
 * grid the declaration renders in, and the times can't tell us — 2pm-11pm
 * reads as either a day or a night shift. Shift-slot approvals skip this
 * modal entirely and stay on the inline button.
 */
export function ApproveDeclarationModal({
  declarationId, workingName, dateLabel, hoursLabel, onClose, onApproved,
}: ApproveProps) {
  const [section, setSection] = useState<DayPart>('NIGHT');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleApprove() {
    setBusy(true); setError(null);
    try {
      await staffAvailabilityApi.approveDeclaration(declarationId, {
        section,
        notes: notes.trim() || undefined,
      });
      onApproved();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  const sideButtons: SideButton[] = [
    { label: 'Approve', onClick: handleApprove, primary: true, disabled: busy },
    { label: 'Cancel',  onClick: onClose,       disabled: busy },
  ];

  return (
    <OperaWindow
      title="APPROVE CUSTOM HOURS"
      status={`${workingName} · ${dateLabel}`}
      onClose={onClose}
      sideButtons={sideButtons}
    >
      {error && <div className="modal-error">{error}</div>}

      <OperaSection title="Requested">
        <p style={{ margin: '4px 0 10px', fontSize: 12 }}>
          <strong>{workingName}</strong> asked to work <strong>{hoursLabel}</strong> on {dateLabel}.
        </p>
      </OperaSection>

      <OperaSection title="Section">
        <OperaField label="Show in row" required hint="Which row of the week grid">
          <select
            value={section}
            onChange={e => setSection(e.target.value as DayPart)}
            autoFocus
          >
            {SECTION_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </OperaField>
      </OperaSection>

      <OperaSection title="Notes">
        <OperaField label="Approval notes">
          <input
            type="text"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="optional"
            maxLength={200}
          />
        </OperaField>
      </OperaSection>
    </OperaWindow>
  );
}

// ---------------------------------------------------------------------------

interface RejectProps {
  declarationId: string;
  workingName: string;
  dateLabel: string;
  onClose: () => void;
  onRejected: () => void;
}

export function RejectDeclarationModal({
  declarationId, workingName, dateLabel, onClose, onRejected,
}: RejectProps) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleConfirm() {
    if (!reason.trim()) {
      setError('Rejection reason is required.');
      return;
    }
    setBusy(true); setError(null);
    try {
      await staffAvailabilityApi.rejectDeclaration(declarationId, reason.trim());
      onRejected();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  const sideButtons: SideButton[] = [
    { label: 'Reject', onClick: handleConfirm, danger: true, disabled: busy },
    { label: 'Cancel', onClick: onClose,        disabled: busy },
  ];

  return (
    <OperaWindow
      title="REJECT REQUEST"
      status={`${workingName} · ${dateLabel}`}
      onClose={onClose}
      sideButtons={sideButtons}
    >
      {error && <div className="modal-error">{error}</div>}

      <OperaSection title="Reason">
        <p style={{ margin: '4px 0 10px', fontSize: 12 }}>
          The service provider will see this reason in her portal. Keep it
          professional — e.g. "Already covered by another girl" or
          "Hours outside the property's published roster window".
        </p>
        <OperaField label="Reason" required>
          <input
            type="text"
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="required"
            autoFocus
            maxLength={200}
          />
        </OperaField>
      </OperaSection>
    </OperaWindow>
  );
}
